/**
 * ScriptTools.jsx — the two ways a script gets into Studio without typing it
 * paragraph by paragraph: paste an essay and let Rammy split it, or brief
 * Rammy and let it write the draft.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import * as L from "lucide-react";

export function ScriptTools({
  voice, onBlocks, onTitleSuggestion,
  tighten = true, onTightenChange,
  chapterCount = 0, onSuggestChapters, suggestingChapters = false,
}) {
  const [mode, setMode] = useState(null);      // null | "paste" | "draft"
  const [paste, setPaste] = useState("");
  const [brief, setBrief] = useState("");
  const [busy, setBusy] = useState(false);

  const doSplit = async ({ replace }) => {
    const text = paste.trim();
    if (!text) { toast.error("Paste some writing first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/studio/split", { text, voice });
      onBlocks(data.blocks, { replace });
      setPaste("");
      setMode(null);
      toast.success(`Split into ${data.blocks.length} paragraph${data.blocks.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't split that");
    } finally {
      setBusy(false);
    }
  };

  const doDraft = async () => {
    const prompt = brief.trim();
    if (prompt.length < 3) { toast.error("Tell Rammy what to write"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/studio/draft", { prompt, voice }, { timeout: 120000 });
      onBlocks(data.blocks, { replace: true });
      if (data.title) onTitleSuggestion?.(data.title);
      setBrief("");
      setMode(null);
      toast.success("Draft ready — edit anything you like");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rammy couldn't draft that");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[var(--rammy-border)] bg-[var(--rm-surface)] p-4" data-testid="studio-script-tools">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)] mr-auto">
          Script
        </div>
        <button
          type="button"
          onClick={() => setMode(mode === "paste" ? null : "paste")}
          data-active={mode === "paste" ? "true" : "false"}
          className={`btn-pill h-8 px-3 text-[11px] inline-flex items-center gap-1.5 border transition ${
            mode === "paste"
              ? "bg-[var(--rammy-cyan-dark)] text-white border-[var(--rammy-cyan-dark)]"
              : "bg-transparent border-[var(--rammy-border)] text-[var(--rammy-text-sec)] hover:border-[var(--rammy-cyan)]"
          }`}
          data-testid="studio-open-paste"
        >
          <L.ClipboardPaste size={12}/> Paste &amp; split
        </button>
        <button
          type="button"
          onClick={() => setMode(mode === "draft" ? null : "draft")}
          data-active={mode === "draft" ? "true" : "false"}
          className={`btn-pill h-8 px-3 text-[11px] inline-flex items-center gap-1.5 border transition ${
            mode === "draft"
              ? "bg-[var(--rammy-cyan-dark)] text-white border-[var(--rammy-cyan-dark)]"
              : "bg-transparent border-[var(--rammy-border)] text-[var(--rammy-text-sec)] hover:border-[var(--rammy-cyan)]"
          }`}
          data-testid="studio-open-draft"
        >
          <L.Sparkles size={12}/> Ask Rammy to write it
        </button>
      </div>

      {/* Two plain-language controls that belong with the script itself. */}
      <div className="mt-3 pt-3 border-t border-[var(--rammy-border)]/70 flex items-center gap-4 flex-wrap">
        <label className="flex items-center gap-2 cursor-pointer" data-testid="studio-tighten-row">
          <button
            type="button"
            role="switch"
            aria-checked={tighten}
            onClick={() => onTightenChange?.(!tighten)}
            data-testid="studio-tighten-toggle"
            className={`w-9 h-5 rounded-full transition-colors relative shrink-0 ${tighten ? "bg-[var(--rammy-cyan-dark)]" : "bg-[var(--rammy-border)]"}`}
          >
            <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-[var(--rm-surface)] shadow transition-all ${tighten ? "left-[18px]" : "left-0.5"}`}/>
          </button>
          <span className="text-[12px] text-[var(--rammy-text-sec)]">
            <span className="font-medium text-[var(--rammy-text)]">Tighten pacing</span> — trim the dead air so it doesn't drag
          </span>
        </label>

        <button
          type="button"
          onClick={onSuggestChapters}
          disabled={suggestingChapters}
          className="ml-auto btn-pill h-8 px-3 text-[11px] border border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)] inline-flex items-center gap-1.5 disabled:opacity-60"
          data-testid="studio-suggest-chapters"
          title="Let Rammy mark where the subject turns"
        >
          {suggestingChapters ? <L.Loader2 size={12} className="animate-spin"/> : <L.Bookmark size={12}/>}
          {chapterCount ? `Chapters (${chapterCount})` : "Suggest chapters"}
        </button>
      </div>

      {mode === "paste" && (
        <div className="mt-3 space-y-2">
          <Textarea
            value={paste}
            onChange={(e) => setPaste(e.target.value)}
            rows={5}
            placeholder="Drop an essay, a newsletter, a chapter — Rammy breaks it into paragraphs at the natural pauses."
            className="rounded-xl text-sm border-[var(--rammy-border)] focus-visible:border-[var(--rammy-cyan)]"
            data-testid="studio-paste-input"
          />
          <div className="flex items-center gap-2">
            <Button onClick={() => doSplit({ replace: true })} disabled={busy} className="btn-pill h-8 text-[11px]" data-testid="studio-paste-replace">
              {busy ? <L.Loader2 size={12} className="animate-spin"/> : <L.Scissors size={12}/>} Replace script
            </Button>
            <Button onClick={() => doSplit({ replace: false })} disabled={busy} variant="outline" className="btn-pill h-8 text-[11px]" data-testid="studio-paste-append">
              <L.Plus size={12}/> Append
            </Button>
            <span className="text-[11px] text-[var(--rammy-text-muted)]">{paste.length.toLocaleString()} characters</span>
          </div>
        </div>
      )}

      {mode === "draft" && (
        <div className="mt-3 space-y-2">
          <Input
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doDraft(); } }}
            placeholder="e.g. a 90-second wind-down about letting go of a hard day"
            className="rounded-xl h-9 text-sm border-[var(--rammy-border)] focus-visible:border-[var(--rammy-cyan)]"
            maxLength={600}
            data-testid="studio-draft-input"
          />
          <div className="flex items-center gap-2">
            <Button onClick={doDraft} disabled={busy} className="btn-pill h-8 text-[11px]" data-testid="studio-draft-go">
              {busy ? <L.Loader2 size={12} className="animate-spin"/> : <L.Sparkles size={12}/>}
              {busy ? "Writing…" : "Write the draft"}
            </Button>
            <span className="text-[11px] text-[var(--rammy-text-muted)]">Replaces the current script — you can undo.</span>
          </div>
        </div>
      )}
    </div>
  );
}
